import React, { useContext, useEffect, useState } from "react";

import { Grid } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";

import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import EditIcon from "@mui/icons-material/Edit";

import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";

import { UserContext } from "App";

import { useDispatch } from "react-redux";

import { GetNewsDetails } from "APIs/NewsAPIs";

import { useParams, useNavigate } from "react-router-dom";

// Receipt page theme (shared)
import SectionCard, {
  SectionHeader,
  ranuOutlinedButtonSx,
  useAccent,
} from "layouts/receipt/components/SectionCard";

import FormField from "layouts/receipt/components/FormField";
import { Loader } from "components/Custom/Loader";

const goldHoverSx = ({ palette }) => ({
  "&:hover": {
    backgroundColor: palette.secondary.main,
    borderColor: palette.secondary.main,
    color: palette.white.main,
    boxShadow: "none",
  },
});

const compactCardsSx = {
  "& > div": {
    padding: { xs: "12px", md: "16px" },
  },
};

// Same look as a disabled MDInput (grey background, faded text), but the field stays
// scrollable. MDInput sets `pointer-events: none` on disabled fields, so a long
// description could not be scrolled with the mouse / touch.
const readOnlyLookSx = ({ palette }) => ({
  backgroundColor: `${palette.grey[200]} !important`,
  "& .MuiInputBase-input": {
    color: palette.text.disabled,
    WebkitTextFillColor: palette.text.disabled,
    overflowY: "auto !important",
  },
});

const ViewNews = () => {
  const userData = useContext(UserContext);

  const navigate = useNavigate();

  const dispatch = useDispatch();

  const { id } = useParams();

  const { accentName } = useAccent();

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const [newsDetails, setNewsDetails] = useState(null);
  const [isFetchingData, setIsFetchingData] = useState(true);

  const getNewsDetailsFunc = async (newsId, access_token, dispatchFunc) => {
    setIsFetchingData(true);
    const response = await GetNewsDetails(newsId, access_token, dispatchFunc);

    if (response !== null) {
      setNewsDetails(response);
    }
    setIsFetchingData(false);
  };

  useEffect(() => {
    if (userData?.access_token !== undefined) {
      getNewsDetailsFunc(id, userData.access_token, dispatch);
    }
  }, [userData, id]);

  // Description text with real line breaks
  // The API sends them as a literal "\n"
  const formatDescription = (text) => {
    if (!text) return "No description provided.";

    return text.replace(/\\n/g, "\n");
  };

  return (
    <MDBox px={{ xs: 0, md: 1 }} pt={{ xs: 0, md: 1 }} pb={3}>
      <SectionHeader
        icon="visibility"
        label="VIEW NEWS"
        startAction={
          <MDButton
            variant="outlined"
            color={accentName}
            size="medium"
            sx={ranuOutlinedButtonSx}
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate("/news")}
          >
            Back
          </MDButton>
        }
        endAction={
          !isMobile && (
            <MDButton
              color="primary"
              size="medium"
              sx={[ranuOutlinedButtonSx, goldHoverSx]}
              startIcon={<EditIcon />}
              onClick={() => navigate(`/news/edit/${id}`)}
            >
              Edit News
            </MDButton>
          )
        }
      />
      {isFetchingData ? (
        <Loader />
      ) : (
        <>
          <MDBox sx={compactCardsSx}>
            <SectionCard title="Details">
              <Grid container spacing={2}>
                <FormField name="title" full>
                  <MDInput disabled fullWidth value={newsDetails?.news_title || ""} />
                </FormField>

                <FormField name="description" full>
                  <MDInput
                    fullWidth
                    multiline
                    minRows={4}
                    maxRows={14}
                    value={formatDescription(newsDetails?.news_description)}
                    InputProps={{ readOnly: true }}
                    sx={readOnlyLookSx}
                  />
                </FormField>
              </Grid>
            </SectionCard>
          </MDBox>

          {/* Mobile Edit Button */}
          {isMobile && (
            <MDBox
              sx={{
                display: "flex",
                justifyContent: "center",
                mt: 2,
              }}
            >
              <MDButton
                color="primary"
                size="medium"
                sx={[ranuOutlinedButtonSx, goldHoverSx]}
                startIcon={<EditIcon />}
                onClick={() => navigate(`/news/edit/${id}`)}
              >
                Edit News
              </MDButton>
            </MDBox>
          )}
        </>
      )}
    </MDBox>
  );
};

export default ViewNews;
